import {
  CURRENCY_CONFIG,
  DELIVERY_TYPE,
  LOCAL_STORAGE_KEY__LATEST_PAYMENT_METHOD,
  PAYMENT_METHODS
} from '@/mixins/constants'
import * as ghnHelper from '@/plugins/ghn-helper'
import { filter, pick, sumBy } from '@/plugins/lodash'
import { getMaxStopPointByService, setItemStorage } from '@/plugins/util'
import * as nationwideAPI from '@/store/api/nationwide'
import * as types from '../mutation-types'

const pathFieldsToEstimateFee = [
  'address',
  'short_address',
  'name',
  'mobile',
  'remarks',
  'tracking_number',
  'district_id',
  'ward_code',
  'note_code',
  'weight',
  'lat',
  'lng',
  'cod',
  'insurance_fee'
]

const pathFieldsToCreateOrder = [
  'address',
  'short_address',
  'name',
  'mobile',
  'remarks',
  'tracking_number',
  'district_id',
  'ward_code',
  'note_code',
  'weight',
  'total_pay',
  'total_fee',
  'discount',
  'distance_fee',
  'item_name',
  'item_order_id',
  'lat',
  'lng',
  'cod',
  'insurance_fee'
]

// nationwide-order-form can use all state of order-form module
const state = {
  ghnGeoData: null,
  path: [], // use same path of other services,
  nationwidePickupCity: null,

  nationwideEstimatingFee: false,
  nationwideEstimatedFeeError: null,

  nationwideCreating: false,
  package_detail: []
}

const getters = {
  ghnGeoData: (state) => state.ghnGeoData,
  isSelectedNationwideService: (state) => {
    return state.service && state.service.delivery_type === DELIVERY_TYPE.NATIONWIDE
  },
  isEstimatingNationwideFee: (state) => state.nationwideEstimatingFee,
  isCreatingNationwideOrder: (state) => state.nationwideCreating,

  isValidNationwideOrderForm: (state, rootState) => {
    // return state.service && state.service.delivery_type === DELIVERY_TYPE.NATIONWIDE && true
    // Note: order of errors is important(service/path/cod/estimate/payment)
    let result = {}
    const service = state.service || {}

    // Check require service
    if (!service._id) {
      result.service = {
        invalid: true,
        msg: 'new_order.messages.required_service_type'
      }
    }
    // Check require min/max stoppoints

    const path = filter(state.path, (item) => {
      return !item.disabled && item.is_valid
    })

    const pathLength = path.length

    if (service.require_to) {
      const numOfStoppoint = pathLength - 1
      const userProfile = rootState.user && rootState.user.profile
      const maxStoppoint = getMaxStopPointByService(service, userProfile)
      const minStoppoint = service.min_stop_points || 1

      if (numOfStoppoint < minStoppoint) {
        result.path = {
          invalid: true,
          msg: {
            key: 'new_order.messages.required_min_stoppoints_v2',
            data: {
              service: service.name,
              n: minStoppoint
            }
          }
        }
      } else if (numOfStoppoint > maxStoppoint) {
        result.path = {
          invalid: true,
          warning: true,
          msg: {
            key: 'new_order.messages.required_max_stoppoints_v2',
            data: {
              service: service.name,
              n: maxStoppoint
            },
            color: 'red'
          }
        }
      }
    } else if (pathLength !== 1) {
      result.path = {
        invalid: true,
        msg: {
          key: 'new_order.messages.required_dropoff_for_flashsale'
        }
      }
    }

    // check valid package_detail
    if (!state.package_detail) {
      result.package_detail = {
        invalid: true,
        msg: {
          key: 'Vui lòng nhập thông tin gói hàng'
        }
      }
    }

    // Check valid cod
    const maxCODPerStoppoint = service.max_cod_per_stop_point || service.cod
    const maxCODPerOrder = service.max_cod

    const sumOfCOD = sumBy(path, 'cod')
    if (maxCODPerOrder > 0 && sumOfCOD > maxCODPerOrder) {
      const currencyPrefix = CURRENCY_CONFIG && CURRENCY_CONFIG.prefix
      result.cod = {
        invalid: true,
        warning: true,
        msg: {
          key: 'new_order.messages.invalid_max_cod',
          data: { cod: `${currencyPrefix}${maxCODPerOrder.toLocaleString()}` },
          color: 'red'
        }
      }
    }

    path.some((p) => {
      if (!maxCODPerOrder && p.cod > 0) {
        result.cod = {
          invalid: true,
          warning: true,
          msg: {
            key: 'dropoff_address.not_supported_cod',
            color: 'red'
          }
        }
        return true
      } else if (maxCODPerStoppoint > 0 && p.cod > maxCODPerStoppoint) {
        const currencyPrefix = CURRENCY_CONFIG && CURRENCY_CONFIG.prefix
        result.cod = {
          invalid: true,
          warning: true,
          msg: {
            key: 'dropoff_address.form.invalid_cod_due_to_max_cod_per_stop_point',
            data: {
              max_cod_per_stop_point: `${currencyPrefix}${maxCODPerStoppoint.toLocaleString()}`
            },
            color: 'red'
          }
        }
        return true
      } else return false
    })

    // Check estimate fee error
    if (state.nationwideEstimatedFeeError) {
      let key = 'estimated_fee.estimate_fee_error'
      let error = ''

      result.estimate = {
        invalid: true,
        msg: {
          key,
          data: { error },
          color: 'red'
        }
      }
    }

    return Object.keys(result).length === 0 ? true : result
  },
  nationwidePackageDetail: (state) => state.package_detail
}

const actions = {
  async getGHNData({ commit }) {
    const data = await ghnHelper.loadGHNData()
    if (data && data.length > 0) {
      commit(types.SET_GHN_GEO_DATA, data)
    }
    return data
  },
  // setGHNGeoData({ commit }, data) {
  //   commit(types.SET_GHN_GEO_DATA, data);
  // },
  getAddressAdministrative({ state }, data) {
    const params = {
      point: {
        lattitude: data.point.lattitude,
        longitude: data.point.longitude
      }
    }

    return new Promise((resolve, reject) => {
      nationwideAPI
        .geoFindAdministrative(params)
        .then((response) => {
          if (state.ghnGeoData) {
            const result = ghnHelper.getGHNIds(state.ghnGeoData, response)
            resolve(result)
          }
        })
        .catch((e) => {
          reject(e)
        })
    })
  },
  // eslint-disable-next-line
  geocodeAdress({}, data) {
    return new Promise((resolve, reject) => {
      nationwideAPI
        .geocodeAddress(data.params)
        .then((response) => {
          resolve(response)
        })
        .catch((e) => {
          reject(e)
        })
    })
  },

  setCreateStatusForNationwidePath({ commit, state }, data) {
    let path = state.path
    for (let index = 1; index < path.length; index++) {
      path[index]['create_status'] = {
        ...data[index - 1],
        is_success: data[index - 1].status_code === 1
      }
    }
    commit(types.SET_ORDER_FORM_PATH, path)
  },
  getNationwideEstimatedFee({ dispatch, commit, state, rootGetters }, config = {}) {
    return new Promise((resolve, reject) => {
      // reset estimated fee
      let path = [...state.path]
      let packageDetails = []

      path.forEach((p) => {
        delete p.estimated_fee
      })

      commit(types.SET_ORDER_FORM_PATH, path)

      // clean order form path for estimating fee
      let cleanedPath = []
      path.forEach((p, index) => {
        if (index === 0) {
          p.district_id = 0
          p.ward_code = ''
          p.note_code = ''
          p.weight = 0
        }
        const cleanedPoint = pick(p, pathFieldsToEstimateFee)

        // update text_address
        cleanedPath.push(cleanedPoint)

        packageDetails.push({ ...p.package_detail, index })
      })

      if (packageDetails && packageDetails.length > 1) {
        packageDetails.shift()
      }
      dispatch('setNationwidePackageDetail', packageDetails)

      // Use method CASH for estimate
      const payment_method = config.usePaymentMethodCash
        ? PAYMENT_METHODS.CASH
        : state.payment_method

      let params = {
        token: rootGetters.token,
        service_id: state.service._id,
        payment_method,
        path: cleanedPath,
        package_detail: packageDetails
      }

      // Add param to make estimate fee with suggested promotion
      if (config.autoRedeemPromoCode) {
        if (!state.promoCode) {
          params.suggest_promotion = true
        }
        dispatch('updateStatusAutoRedeemedPromoCode', true)
      }

      if (state.promoCode) {
        params.promo_code = state.promoCode
      }

      commit(types.SET_NATIONWIDE_ESTIMATING_FEE, true)
      commit(types.SET_NATIONWIDE_ESTIMATED_FEE_ERROR, null)
      nationwideAPI
        .estimatedFee(params)
        .then((response) => {
          // sum total fee
          let orderFee = {},
            path = state.path
          response.forEach((point, index) => {
            if (path[index + 1]) {
              path[index + 1]['estimated_fee'] = { ...point }
            }

            for (let key in point) {
              if (typeof point[key] === 'number') {
                if (orderFee[key] === undefined) {
                  orderFee[key] = 0
                }
                if (key === 'total_balance' && point[key]) {
                  orderFee[key] = point[key]
                } else {
                  orderFee[key] += point[key]
                }
              }

              if (
                (key === 'payment_error_message' ||
                  key === 'payment_method' ||
                  key === 'suggested_promotion') &&
                point[key]
              ) {
                orderFee[key] = point[key]
              }
            }
          })

          commit(types.SET_ORDER_FORM_PATH, path)
          commit(types.ESTIMATE_FEE_SUCCESS, orderFee)
          commit(types.SET_NATIONWIDE_ESTIMATING_FEE, false)

          if (orderFee.suggested_promotion) {
            dispatch('updateStatusAutoRedeemedPromoCode', orderFee.suggested_promotion)
            dispatch('setOrderFormPromoCode', orderFee.suggested_promotion)
          }

          resolve(response)
        })
        .catch((e) => {
          commit(types.SET_NATIONWIDE_ESTIMATING_FEE, false)
          commit(types.SET_NATIONWIDE_ESTIMATED_FEE_ERROR, e)
          reject(e)
        })
    })
  },
  createNationwideOrder({ commit, state, rootGetters, dispatch }) {
    return new Promise((resolve, reject) => {
      let path = []
      state.path.forEach((p, index) => {
        if (!p.create_status || !p.create_status.is_success) {
          let temp = index === 0 ? p : Object.assign({}, p, p.estimated_fee)
          const cleanedPoint = pick(temp, pathFieldsToCreateOrder)
          path.push(cleanedPoint)
        }
      })

      let params = {
        token: rootGetters.token,
        service_id: state.service._id,
        path: path,
        package_detail: state.package_detail
      }

      if (state.promoCode) {
        params.promo_code = state.promoCode
      }

      if (state.payment_method) {
        params.payment_method = state.payment_method

        setItemStorage(LOCAL_STORAGE_KEY__LATEST_PAYMENT_METHOD, state.payment_method)
      }

      commit(types.SET_NATIONWIDE_CREATING, true)
      nationwideAPI
        .create(params)
        .then((response) => {
          // sum total fee
          let orderFee = {}
          response.forEach((point) => {
            for (let key in point) {
              if (typeof point[key] === 'number') {
                if (orderFee[key] === undefined) {
                  orderFee[key] = 0
                }

                orderFee[key] += point[key]
              }
            }
          })

          commit(types.SET_NATIONWIDE_CREATING, false)
          dispatch('updateStatusShowedAddBankModal', false)
          dispatch('updateStatusAutoRedeemedPromoCode', false)

          resolve(response)
        })
        .catch((e) => {
          commit(types.SET_NATIONWIDE_CREATING, false)
          // const error = (e && e.response) || null;
          reject(e)
        })
    })
  },
  onCreateNationwideOrderFailure({ commit, state }, data) {
    let path = state.path
    for (let index = 1; index < path.length; index++) {
      const response = data[index - 1]
      path[index]['create_status'] = {
        ...response,
        is_success: response.status_code === 1
      }
    }

    // console.log('path', path)
    commit(types.SET_ORDER_FORM_PATH, path)
  },
  findNationwidePickupCity({ commit, state, getters, dispatch }) {
    const pickupPoint = state.path[0]

    if (pickupPoint && pickupPoint.lat && pickupPoint.lng && getters.isSelectedNationwideService) {
      const params = {
        point: {
          lattitude: pickupPoint.lat,
          longitude: pickupPoint.lng
        },
        fields: { level: 1, name: 1 }
      }

      dispatch('getAddressAdministrative', params)
        .then((detail) => {
          if (detail && detail.city) {
            commit(types.UPDATE_NATIONWIDE_PICKUP_CITY, detail.city.code)
          }
        })
        .catch(() => {
          console.log('Cannot find city id of pickup point - nationwide')
        })
    }
  },
  setNationwidePackageDetail({ commit }, data) {
    commit(types.SET_NATIONWIDE_PACKAGE_DETAIL, data)
  }
}

const mutations = {
  [types.SET_GHN_GEO_DATA](state, data) {
    state.ghnGeoData = data
  },
  [types.SET_NATIONWIDE_ESTIMATING_FEE](state, isEstimating) {
    if (isEstimating) {
      state.estimatedFee = {}
    }

    state.nationwideEstimatingFee = isEstimating
  },
  [types.SET_NATIONWIDE_ESTIMATED_FEE_ERROR](state, data) {
    state.nationwideEstimatedFeeError = data
  },
  [types.SET_NATIONWIDE_CREATING](state, data) {
    state.nationwideCreating = data
  },
  [types.UPDATE_NATIONWIDE_PICKUP_CITY](state, cityId) {
    state.nationwidePickupCity = cityId
  },
  [types.SET_NATIONWIDE_PACKAGE_DETAIL](state, data) {
    state.package_detail = data
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}
